/**
 * DashboardRenderer
 *
 * Renders a dashboard from menu items with menuType=DASHBOARD.
 * Maps menu items to widgets from the registry and renders them in a grid layout.
 */
import { useMemo, Component } from 'react'
import { Box, Grid, Typography, Alert, LinearProgress, Container } from '@mui/material'

import { useDashboard } from '../../hooks/useDashboard'
import { getWidget, WIDGET_REGISTRY } from './widget-registry'

/**
 * Error boundary to catch widget rendering errors
 */
class WidgetErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Widget error:', error, errorInfo)
  }

  render() {
    const { hasError, error } = this.state
    const { widgetName, children } = this.props

    if (hasError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load widget: {widgetName || 'Unknown'}
          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {error?.message}
            </Typography>
          )}
        </Alert>
      )
    }
    return children
  }
}

/**
 * Renders a single widget based on menu item configuration
 */
function WidgetRenderer({ menuItem, context }) {
  const widgetKey = menuItem.metadata?.widget
  const widgetConfig = getWidget(widgetKey)

  if (!widgetConfig) {
    // Widget not found in registry - show placeholder in dev, hide in prod
    if (process.env.NODE_ENV === 'development') {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Widget &quot;{widgetKey}&quot; not found in registry for menu item &quot;{menuItem.key}&quot;
        </Alert>
      )
    }
    return null
  }

  const WidgetComponent = widgetConfig.component
  const props = {
    ...widgetConfig.defaultProps,
    ...menuItem.metadata?.props,
    ...context, // Pass fund, fundId, etc.
  }

  return (
    <WidgetErrorBoundary widgetName={widgetConfig.label || widgetKey}>
      <WidgetComponent {...props} />
    </WidgetErrorBoundary>
  )
}

/**
 * DashboardRenderer Component
 *
 * @param {string} dashboardKey - Key prefix to filter menu items (e.g., 'advisor', 'dashboard')
 * @param {object} context - Additional context to pass to widgets (fund, fundId, etc.)
 * @param {string} languageCode - Language code for translations (default: 'en')
 */
export function DashboardRenderer({ dashboardKey, context = {}, languageCode = 'en' }) {
  const { menuItems, loading, error } = useDashboard(dashboardKey, languageCode)

  // Sort items by sortOrder
  const sortedItems = useMemo(() => {
    if (!menuItems) return []
    return [...menuItems].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  }, [menuItems])

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading dashboard: {error.message}</Typography>
      </Box>
    )
  }

  if (sortedItems.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No widgets configured for this dashboard.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Use the Dashboard Builder in the admin portal to add widgets.
        </Typography>
      </Box>
    )
  }

  // Separate full-width widgets from regular widgets
  const fullWidthItems = sortedItems.filter((item) => {
    const widgetConfig = getWidget(item.metadata?.widget)
    return widgetConfig?.fullWidth === true
  })
  const regularItems = sortedItems.filter((item) => {
    const widgetConfig = getWidget(item.metadata?.widget)
    return widgetConfig?.fullWidth !== true
  })

  return (
    <Box>
      {/* Full-width widgets rendered outside Container */}
      {fullWidthItems.map((item) => (
        <Box key={item.id}>
          <WidgetRenderer menuItem={item} context={context} />
        </Box>
      ))}

      {/* Regular widgets in centered Container */}
      {regularItems.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Grid container spacing={3}>
            {regularItems.map((item) => {
              const columns = item.metadata?.layout?.columns ?? 12

              return (
                <Grid item xs={12} md={columns} key={item.id} sx={{ py: 2 }}>
                  <WidgetRenderer menuItem={item} context={context} />
                </Grid>
              )
            })}
          </Grid>
        </Container>
      )}
    </Box>
  )
}

/**
 * Get available widgets for a category (for the builder UI)
 */
export function getAvailableWidgets(category) {
  return Object.entries(WIDGET_REGISTRY)
    .filter(([, config]) => !category || config.category === category)
    .map(([key, config]) => ({
      key,
      label: config.label,
      description: config.description,
      category: config.category,
      defaultLayout: config.defaultLayout,
      propsSchema: config.propsSchema,
    }))
}
